import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Crown,
  Scale,
  Star,
  Users,
  UserCog,
  UserPlus,
  UserMinus,
  Clock,
  ArrowRightLeft,
  ClipboardCheck,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth';
import { useMatterTeam, useTeamMutations } from '../hooks';
import { matterKeys, mattersApi } from '../api';
import { CaseK3RatingDialog } from './CaseK3RatingDialog';
import { MatterQaScoringDialog } from './MatterQaScoringDialog';
import { LeadDelegationDialog } from './LeadDelegationDialog';
import { ReassignPartnerDialog } from './ReassignPartnerDialog';
import { ReassignStaffDialog } from './ReassignStaffDialog';
import { AddStaffDialog } from './AddStaffDialog';
import type { Matter, MatterTeamMember, MatterTeamRole } from '../types';

interface MatterTeamPanelProps {
  matter: Matter;
  onChanged?: () => void;
}

function formatHours(hours: number | undefined): string {
  return `${(hours ?? 0).toFixed(1)}h`;
}

function initials(name: string | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

const ROLE_STYLE: Record<MatterTeamRole, { badge: string; avatar: string }> = {
  PARTNER: {
    badge: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    avatar: 'bg-indigo-50 text-indigo-700',
  },
  LEAD: {
    badge: 'border-amber-200 bg-amber-50 text-amber-700',
    avatar: 'bg-amber-50 text-amber-700',
  },
  STAFF: {
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    avatar: 'bg-emerald-50 text-emerald-700',
  },
};

const ROLE_ICON: Record<MatterTeamRole, React.ComponentType<{ className?: string }>> = {
  PARTNER: Scale,
  LEAD: Crown,
  STAFF: Users,
};

/**
 * Case 360° (Stage 14) — the members display is where the team is MANAGED.
 * One table, Partner → Lead → Contributors. Role-level actions (Reassign
 * Partner / Reassign Lead / Add member) sit on top; Contributor rows carry
 * Reassign (their active tasks transfer to the incoming staffer) and Remove.
 */
export function MatterTeamPanel({ matter, onChanged }: MatterTeamPanelProps) {
  const { t } = useTranslation();
  const { hasAccess, user } = useAuth();
  const queryClient = useQueryClient();
  // Server-owned read model — the table renders whatever the endpoint sends.
  const { data: team, isLoading } = useMatterTeam(matter?.id);
  const { removeStaffMutation } = useTeamMutations();

  const [leadDialogOpen, setLeadDialogOpen] = React.useState(false);
  const [partnerDialogOpen, setPartnerDialogOpen] = React.useState(false);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [memberToRemove, setMemberToRemove] = React.useState<MatterTeamMember | null>(null);
  const [memberToReassign, setMemberToReassign] = React.useState<MatterTeamMember | null>(null);
  const [memberToRate, setMemberToRate] = React.useState<MatterTeamMember | null>(null);
  const [memberToQaScore, setMemberToQaScore] =
    React.useState<MatterTeamMember | null>(null);

  // QA scores already on this matter — drives the K1 badge and pre-fills the
  // dialog. Only fetched for closed matters, the only phase QA is allowed in.
  const { data: qaScores } = useQuery({
    queryKey: matterKeys.qa(matter?.id ?? ''),
    queryFn: () => mattersApi.getQa(matter!.id),
    enabled: !!matter?.id && matter?.phase === 'CLOSED',
    staleTime: 30 * 1000,
  });

  const currentUserId = user?.id;
  const globalRoleType = user?.role?.type;
  const isTeamPartner = matter?.partners?.some((p) => p?.id === currentUserId) ?? false;
  const isTeamLead =
    matter?.responsibleAttorneys?.some((a) => a?.id === currentUserId) ?? false;
  // `matter:manage-all` = firm-wide case-management scope (Office Manager);
  // the server-side gates accept it alongside the senior role types.
  const isPartnerOrMD =
    globalRoleType === 'MD' ||
    globalRoleType === 'PARTNER' ||
    globalRoleType === 'MANAGER' ||
    hasAccess('matter', 'manage-all') ||
    isTeamPartner;

  const isClosed = matter?.phase === 'CLOSED';
  const canReassignLead =
    !isClosed && hasAccess('matter', 'delegate') && (isTeamLead || isPartnerOrMD);
  const canReassignPartner =
    !isClosed && hasAccess('matter', 'delegate') && isPartnerOrMD;
  const canAddStaff =
    !isClosed && hasAccess('matter', 'add-staff') && (isTeamLead || isPartnerOrMD);
  const canManageStaff =
    !isClosed && hasAccess('matter', 'remove-staff') && (isTeamLead || isPartnerOrMD);

  // Post-close team rating (K3 review): MD + matter Partner rate everyone,
  // matter Lead rates Contributors only; never yourself; one review per
  // member per matter (whoever rates first — enforced server-side too).
  const isMD = globalRoleType === 'MD';
  const canRateMember = (m: MatterTeamMember) =>
    isClosed &&
    hasAccess('review', 'create') &&
    m?.id !== currentUserId &&
    m?.matterRating == null &&
    (isMD || isTeamPartner || (isTeamLead && m?.role === 'STAFF'));

  // Post-close QA scoring (K1 — the §6.3 Quality_Index source). Same seniority
  // matrix as the K3 review and enforced server-side; unlike K3 it may be
  // re-submitted, so an existing score doesn't hide the button.
  const canQaScoreMember = (m: MatterTeamMember) =>
    isClosed &&
    hasAccess('matter', 'qa') &&
    m?.id !== currentUserId &&
    (isMD || isTeamPartner || (isTeamLead && m?.role === 'STAFF'));

  // Weighted QA average per rated member, from the scores already on record.
  const qaAverageByUser = React.useMemo(() => {
    const acc = new Map<number, { weighted: number; weight: number }>();
    for (const s of qaScores ?? []) {
      const w = s?.criteria?.weight || 1;
      const cur = acc.get(s.ratedUserId) ?? { weighted: 0, weight: 0 };
      cur.weighted += s.score * w;
      cur.weight += w;
      acc.set(s.ratedUserId, cur);
    }
    return new Map(
      [...acc.entries()].map(([id, v]) => [
        id,
        v.weight > 0 ? v.weighted / v.weight : null,
      ]),
    );
  }, [qaScores]);

  const pendingDelegation = team?.pendingDelegation ?? null;
  const hasPendingDelegation = !!pendingDelegation && !pendingDelegation?.acknowledged;

  // Already ordered server-side: Partner → Lead → Contributors.
  const rows: MatterTeamMember[] = team?.members ?? [];

  const roleLabel: Record<MatterTeamRole, string> = {
    PARTNER: t('matterTeam.accountable'),
    LEAD: t('matterTeam.lead'),
    STAFF: t('matterTeam.contributors'),
  };

  const confirmRemove = async () => {
    if (!memberToRemove) return;
    await removeStaffMutation.mutateAsync({
      id: matter.id,
      userId: memberToRemove.id,
    });
    setMemberToRemove(null);
    onChanged?.();
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm">
      <CardHeader className="pb-3 border-b border-slate-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-400" />
            {t('matterTeam.title')}
          </CardTitle>

          {/* Role-level actions */}
          <div className="flex flex-wrap items-center gap-2">
            {canReassignPartner && (
              <Button variant="outline" size="sm" onClick={() => setPartnerDialogOpen(true)}>
                <UserCog className="mr-1.5 h-3.5 w-3.5" />
                {t('matterTeam.reassignPartnerButton')}
              </Button>
            )}
            {canReassignLead && (
              <Button variant="outline" size="sm" onClick={() => setLeadDialogOpen(true)}>
                <UserCog className="mr-1.5 h-3.5 w-3.5" />
                {t('leadDelegation.reassignButton')}
              </Button>
            )}
            {canAddStaff && (
              <Button
                size="sm"
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={() => setAddDialogOpen(true)}
              >
                <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                {t('matterTeam.addMember')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {hasPendingDelegation && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Clock className="h-4 w-4 shrink-0" />
            {t('matterTeam.pendingDelegation')}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2 py-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : rows.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('matterTeam.member')}</TableHead>
                <TableHead>{t('matterTeam.role')}</TableHead>
                <TableHead className="text-center">{t('matterTeam.activeTasks')}</TableHead>
                <TableHead className="text-center">{t('dashboard.utilization')}</TableHead>
                <TableHead className="text-center">{t('dashboard.hours')}</TableHead>
                <TableHead className="text-center">{t('dashboard.pending')}</TableHead>
                <TableHead className="text-right">{t('matterTeam.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((member) => {
                const RoleIcon = ROLE_ICON[member.role];
                return (
                  <TableRow key={`${member.role}-${member.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback
                            className={cn(
                              'text-xs font-semibold',
                              ROLE_STYLE[member.role].avatar,
                            )}
                          >
                            {initials(member?.name ?? undefined)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900">
                            {member?.name}
                          </p>
                          {member?.roleName && (
                            <p className="truncate text-xs text-slate-400">
                              {member.roleName}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('border text-xs', ROLE_STYLE[member.role].badge)}>
                        <RoleIcon className="mr-1 h-3 w-3" />
                        {roleLabel[member.role]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={cn(
                          'text-sm font-semibold',
                          member.activeTasks > 0 ? 'text-slate-800' : 'text-slate-300',
                        )}
                      >
                        {member.activeTasks}
                      </span>
                    </TableCell>
                    <TableCell className="text-center tabular-nums text-sm text-slate-800">
                      {(member?.utilization ?? 0).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-center tabular-nums text-sm text-slate-800">
                      {formatHours(member?.hours)}
                    </TableCell>
                    <TableCell className="text-center tabular-nums text-sm text-slate-500">
                      {formatHours(member?.pendingHours)}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const showManage = member.role === 'STAFF' && canManageStaff;
                        const showRated = isClosed && member?.matterRating != null;
                        const showRate = canRateMember(member);
                        const showQa = canQaScoreMember(member);
                        const memberQa = qaAverageByUser.get(member.id) ?? null;
                        if (!showManage && !showRated && !showRate && !showQa && memberQa == null) {
                          return <span className="text-xs text-slate-300">—</span>;
                        }
                        return (
                          <div className="inline-flex items-center gap-1">
                            {/* K1 — weighted QA score already recorded */}
                            {memberQa != null && (
                              <Badge
                                className="border border-sky-200 bg-sky-50 text-xs text-sky-700"
                                title={t('matterQa.k1Recorded')}
                              >
                                K1 {memberQa.toFixed(1)}
                              </Badge>
                            )}
                            {showQa && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-xs text-sky-600 hover:bg-sky-50 hover:text-sky-700"
                                onClick={() => setMemberToQaScore(member)}
                              >
                                <ClipboardCheck className="mr-1 h-3.5 w-3.5" />
                                {t('matterQa.scoreButton')}
                              </Button>
                            )}
                            {showRated && (
                              <Badge
                                className="border border-amber-200 bg-amber-50 text-amber-700 text-xs"
                                title={t('matterTeam.rated')}
                              >
                                <Star className="mr-1 h-3 w-3 fill-current" />
                                {member?.matterRating}
                              </Badge>
                            )}
                            {showRate && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => setMemberToRate(member)}
                              >
                                <Star className="mr-1 h-3.5 w-3.5" />
                                {t('k3.rate')}
                              </Button>
                            )}
                            {showManage && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                  onClick={() => setMemberToReassign(member)}
                                >
                                  <ArrowRightLeft className="mr-1 h-3.5 w-3.5" />
                                  {t('matterTeam.reassignStaffButton')}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                                  title={t('matterTeam.removeConfirm')}
                                  onClick={() => setMemberToRemove(member)}
                                >
                                  <UserMinus className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <p className="py-6 text-center text-sm text-slate-400">{t('noneAssigned')}</p>
        )}
      </CardContent>

      {/* Reassign Lead (Stage 2 delegate / force-assign) */}
      <LeadDelegationDialog
        open={leadDialogOpen}
        onOpenChange={setLeadDialogOpen}
        matter={matter ?? null}
        canForce={isPartnerOrMD}
        onSuccess={onChanged}
      />

      {/* Reassign Partner (Stage 14) */}
      <ReassignPartnerDialog
        open={partnerDialogOpen}
        onOpenChange={setPartnerDialogOpen}
        matter={matter ?? null}
        onSuccess={onChanged}
      />

      {/* Add Contributor (Stage 14) */}
      <AddStaffDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        matter={matter ?? null}
        onSuccess={onChanged}
      />

      {/* Replace a Contributor — their active tasks transfer to the incoming staffer */}
      <ReassignStaffDialog
        open={!!memberToReassign}
        onOpenChange={(open) => {
          if (!open) setMemberToReassign(null);
        }}
        matter={matter ?? null}
        fromUser={memberToReassign}
        onSuccess={onChanged}
      />

      {/* Post-close per-member QA scoring (K1 — the §6.3 Quality_Index source) */}
      <MatterQaScoringDialog
        open={!!memberToQaScore}
        onOpenChange={(open) => {
          if (!open) setMemberToQaScore(null);
        }}
        matterId={matter?.id}
        person={
          memberToQaScore
            ? { id: memberToQaScore.id, name: memberToQaScore?.name ?? '' }
            : null
        }
        raterId={currentUserId}
        onSuccess={onChanged}
      />

      {/* Post-close per-member team rating (K3 review) */}
      <CaseK3RatingDialog
        open={!!memberToRate}
        onOpenChange={(open) => {
          if (!open) setMemberToRate(null);
        }}
        matter={matter ?? null}
        person={
          memberToRate ? { id: memberToRate.id, name: memberToRate?.name ?? '' } : null
        }
        onSuccess={() => {
          if (matter?.id) {
            void queryClient.invalidateQueries({ queryKey: matterKeys.team(matter.id) });
          }
          onChanged?.();
        }}
      />

      {/* Remove Contributor confirm */}
      <AlertDialog
        open={!!memberToRemove}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('matterTeam.removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('matterTeam.removeDescription', { name: memberToRemove?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setMemberToRemove(null)}
              disabled={removeStaffMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemove}
              disabled={removeStaffMutation.isPending}
            >
              {removeStaffMutation.isPending
                ? t('processing')
                : t('matterTeam.removeConfirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
